import type { JSX } from 'react';
import './Wordmark.css';

interface WordmarkProps {
  /** Italic-small-caps line above the wordmark ("The Village of"). */
  preamble?: string;
  /** The wordmark itself ("Susdle"). */
  title: string;
}

/**
 * Chapbook frontispiece title plate. Two stacked lines and a divider:
 *
 *     The Village of      ← italic-small-caps preamble
 *      SUSDLE             ← display wordmark
 *      ——————             ← hand-drawn frontispiece rule (wordmark-divider.png)
 *
 * The italic-small-caps preamble + roman uppercase title is the genuine
 * chapbook title-page idiom (italic intro, roman title). An earlier
 * three-line variant carried "of" on its own line ("Tales / of / the
 * Curious") but the floating connector read as a whisper against the
 * 60–80px wordmark; the connector now lives on the preamble line.
 * `preamble` is optional so non-spread surfaces can render the wordmark
 * alone.
 *
 * The subtitle slot under the divider is deleted on purpose — a tagline-
 * shaped line beneath the wordmark is the most saturated AI-design
 * template. The visual world (page frame, village illustration, banquet
 * grid) carries the parlour-game register without language.
 */
export function Wordmark({ preamble, title }: WordmarkProps): JSX.Element {
  return (
    <header className="susdle-wordmark" data-stacked={preamble ? 'yes' : 'no'}>
      {preamble ? <p className="susdle-wordmark__preamble">{preamble}</p> : null}
      <h1 className="susdle-wordmark__title susdle-display">{title}</h1>
      <span className="susdle-wordmark__divider" aria-hidden />
    </header>
  );
}
